import React, { useCallback, useEffect, useState } from 'react';
import {
  approveUser,
  getUserDetail,
  listUsers,
  rejectUser,
} from '../../services/adminUserService';
import './UserManagement.css';

const APPROVE_ROLES_USER = 'USER';
const APPROVE_ROLES_ADMIN = 'ADMIN';

const FILTERS = [
  { value: '', label: 'All users' },
  { value: 'PENDING', label: 'Pending access' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
];

function statusClass(s) {
  if (s === 'PENDING') return 'user-mgmt__badge--pending';
  if (s === 'APPROVED') return 'user-mgmt__badge--approved';
  if (s === 'REJECTED') return 'user-mgmt__badge--rejected';
  return '';
}

function userInitials(firstName, lastName) {
  const f = (firstName || '').trim();
  const l = (lastName || '').trim();
  const two = `${f.charAt(0)}${l.charAt(0)}`.toUpperCase();
  return two || '?';
}

function emptyDash(v) {
  if (v == null) return '—';
  const s = String(v).trim();
  return s || '—';
}

export default function UserManagement() {
  const [filter, setFilter] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionBusy, setActionBusy] = useState(null);
  const [approveRole, setApproveRole] = useState(APPROVE_ROLES_USER);

  useEffect(() => {
    if (detail?.status === 'PENDING') {
      setApproveRole(APPROVE_ROLES_USER);
    }
  }, [detail?.id, detail?.status]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await listUsers(filter || undefined);
      setUsers(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load users.');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const openDetail = async (id) => {
    setDetailLoading(true);
    setDetail(null);
    try {
      const d = await getUserDetail(id);
      setDetail(d);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load user.');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => setDetail(null);

  const onApprove = async (id, role = APPROVE_ROLES_USER) => {
    setActionBusy(id);
    setError('');
    try {
      await approveUser(id, role);
      await load();
      setDetail(null);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Approve failed.');
    } finally {
      setActionBusy(null);
    }
  };

  const onReject = async (id) => {
    setActionBusy(id);
    setError('');
    try {
      await rejectUser(id);
      await load();
      setDetail(null);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Reject failed.');
    } finally {
      setActionBusy(null);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  return (
    <div className="user-mgmt">
      <div className="user-mgmt__header">
        <h1 className="user-mgmt__title">User management</h1>
        <p className="user-mgmt__lead">
          Review access requests and manage accounts. Pending users need approval before they can sign in.
        </p>
      </div>

      {error ? <div className="user-mgmt__error">{error}</div> : null}

      <div className="user-mgmt__filters" role="tablist" aria-label="Filter by status">
        {FILTERS.map((f) => (
          <button
            key={f.value || 'all'}
            type="button"
            role="tab"
            aria-selected={filter === f.value}
            className={`user-mgmt__filter${filter === f.value ? ' user-mgmt__filter--active' : ''}`}
            onClick={() => setFilter(f.value)}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="user-mgmt__table-wrap">
        {loading ? (
          <p className="user-mgmt__muted">Loading users…</p>
        ) : (
          <table className="user-mgmt__table">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Requested</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan={6} className="user-mgmt__muted">
                    No users match this filter.
                  </td>
                </tr>
              ) : (
                users.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <span className="user-mgmt__name">
                        {u.firstName} {u.lastName}
                      </span>
                      <span className="user-mgmt__username">@{u.username}</span>
                    </td>
                    <td>{u.email}</td>
                    <td>{u.role}</td>
                    <td>
                      <span className={`user-mgmt__badge ${statusClass(u.status)}`}>{u.status}</span>
                    </td>
                    <td>{fmtDate(u.createdAt)}</td>
                    <td className="user-mgmt__actions">
                      <button type="button" className="user-mgmt__btn user-mgmt__btn--ghost" onClick={() => openDetail(u.id)}>
                        View
                      </button>
                      {u.status === 'PENDING' ? (
                        <>
                          <button
                            type="button"
                            className="user-mgmt__btn user-mgmt__btn--approve"
                            disabled={actionBusy === u.id}
                            title="Approves as a standard user. Open detail to grant Administrator."
                            onClick={() => onApprove(u.id, APPROVE_ROLES_USER)}
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            className="user-mgmt__btn user-mgmt__btn--reject"
                            disabled={actionBusy === u.id}
                            onClick={() => onReject(u.id)}
                          >
                            Reject
                          </button>
                        </>
                      ) : null}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {(detail || detailLoading) && (
        <div className="user-mgmt__modal-backdrop" role="presentation" onClick={closeDetail}>
          <div
            className="user-mgmt__modal user-mgmt__modal--profile"
            role="dialog"
            aria-modal="true"
            aria-labelledby="user-detail-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="user-mgmt__modal-topbar">
              <span className="user-mgmt__modal-eyebrow">User profile</span>
              <button type="button" className="user-mgmt__modal-close" onClick={closeDetail} aria-label="Close dialog">
                <span aria-hidden="true">×</span>
              </button>
            </div>
            {detailLoading ? (
              <div className="user-mgmt__modal-loading" aria-busy="true" aria-live="polite">
                <div className="user-mgmt__modal-loading-bar" />
                <p className="user-mgmt__modal-loading-text">Loading profile…</p>
              </div>
            ) : detail ? (
              <>
                <header className="user-mgmt__modal-hero">
                  <div className="user-mgmt__modal-avatar" aria-hidden="true">
                    {userInitials(detail.firstName, detail.lastName)}
                  </div>
                  <div className="user-mgmt__modal-hero-main">
                    <h2 id="user-detail-title" className="user-mgmt__modal-name">
                      {[detail.firstName, detail.lastName].filter(Boolean).join(' ') || detail.username}
                    </h2>
                    <p className="user-mgmt__modal-handle">@{detail.username}</p>
                    <p className="user-mgmt__modal-email">{emptyDash(detail.email)}</p>
                  </div>
                  <div className="user-mgmt__modal-hero-meta">
                    <span className={`user-mgmt__badge ${statusClass(detail.status)}`}>{detail.status}</span>
                    <span
                      className={
                        detail.role === 'ADMIN'
                          ? 'user-mgmt__pill user-mgmt__pill--admin'
                          : 'user-mgmt__pill user-mgmt__pill--user'
                      }
                    >
                      {detail.role === 'ADMIN' ? 'Administrator' : 'Standard user'}
                    </span>
                  </div>
                </header>

                <div className="user-mgmt__modal-body">
                  <section className="user-mgmt__detail-section" aria-labelledby="umd-contact">
                    <h3 id="umd-contact" className="user-mgmt__detail-heading">
                      Contact
                    </h3>
                    <dl className="user-mgmt__detail-dl">
                      <div className="user-mgmt__detail-row">
                        <dt>Phone</dt>
                        <dd>{emptyDash(detail.phone)}</dd>
                      </div>
                    </dl>
                  </section>

                  <section className="user-mgmt__detail-section" aria-labelledby="umd-address">
                    <h3 id="umd-address" className="user-mgmt__detail-heading">
                      Address
                    </h3>
                    <dl className="user-mgmt__detail-dl">
                      <div className="user-mgmt__detail-row user-mgmt__detail-row--full">
                        <dt>Street</dt>
                        <dd>{emptyDash(detail.streetAddress)}</dd>
                      </div>
                      <div className="user-mgmt__detail-row">
                        <dt>City</dt>
                        <dd>{emptyDash(detail.city)}</dd>
                      </div>
                      <div className="user-mgmt__detail-row">
                        <dt>State</dt>
                        <dd>{emptyDash(detail.state)}</dd>
                      </div>
                      <div className="user-mgmt__detail-row">
                        <dt>ZIP</dt>
                        <dd>{emptyDash(detail.zip)}</dd>
                      </div>
                    </dl>
                  </section>

                  <section
                    className="user-mgmt__detail-section user-mgmt__detail-section--restricted"
                    aria-labelledby="umd-id"
                  >
                    <h3 id="umd-id" className="user-mgmt__detail-heading">
                      Identification
                      <span className="user-mgmt__detail-tag">Sensitive</span>
                    </h3>
                    <dl className="user-mgmt__detail-dl">
                      <div className="user-mgmt__detail-row user-mgmt__detail-row--full">
                        <dt>Passport</dt>
                        <dd>
                          <span className="user-mgmt__sensitive">{emptyDash(detail.passportNumber)}</span>
                        </dd>
                      </div>
                      <div className="user-mgmt__detail-row user-mgmt__detail-row--full">
                        <dt>SSN</dt>
                        <dd>
                          <span className="user-mgmt__sensitive">{emptyDash(detail.ssn)}</span>
                        </dd>
                      </div>
                    </dl>
                  </section>

                  <section className="user-mgmt__detail-section" aria-labelledby="umd-account">
                    <h3 id="umd-account" className="user-mgmt__detail-heading">
                      Account
                    </h3>
                    <dl className="user-mgmt__detail-dl">
                      <div className="user-mgmt__detail-row user-mgmt__detail-row--full">
                        <dt>Requested</dt>
                        <dd>{fmtDate(detail.createdAt)}</dd>
                      </div>
                      {detail.approvedAt ? (
                        <div className="user-mgmt__detail-row user-mgmt__detail-row--full">
                          <dt>Last decision</dt>
                          <dd>
                            {fmtDate(detail.approvedAt)}
                            {detail.approvedByUsername ? (
                              <span className="user-mgmt__detail-sub"> · by {detail.approvedByUsername}</span>
                            ) : null}
                          </dd>
                        </div>
                      ) : null}
                    </dl>
                  </section>

                  {detail.status === 'PENDING' ? (
                    <div className="user-mgmt__modal-footer">
                      <div className="user-mgmt__approve-panel" role="group" aria-label="Role after approval">
                        <p className="user-mgmt__approve-panel-title">Role after approval</p>
                        <p className="user-mgmt__approve-panel-hint">Admins can access user management and approve others.</p>
                        <div className="user-mgmt__approve-cards">
                          <label
                            className={
                              approveRole === APPROVE_ROLES_USER
                                ? 'user-mgmt__approve-card user-mgmt__approve-card--active'
                                : 'user-mgmt__approve-card'
                            }
                          >
                            <input
                              type="radio"
                              name={`approve-role-${detail.id}`}
                              className="user-mgmt__approve-card-input"
                              checked={approveRole === APPROVE_ROLES_USER}
                              onChange={() => setApproveRole(APPROVE_ROLES_USER)}
                            />
                            <span className="user-mgmt__approve-card-title">Standard user</span>
                            <span className="user-mgmt__approve-card-desc">Default product access</span>
                          </label>
                          <label
                            className={
                              approveRole === APPROVE_ROLES_ADMIN
                                ? 'user-mgmt__approve-card user-mgmt__approve-card--active'
                                : 'user-mgmt__approve-card'
                            }
                          >
                            <input
                              type="radio"
                              name={`approve-role-${detail.id}`}
                              className="user-mgmt__approve-card-input"
                              checked={approveRole === APPROVE_ROLES_ADMIN}
                              onChange={() => setApproveRole(APPROVE_ROLES_ADMIN)}
                            />
                            <span className="user-mgmt__approve-card-title">Administrator</span>
                            <span className="user-mgmt__approve-card-desc">Full admin tools</span>
                          </label>
                        </div>
                      </div>
                      <div className="user-mgmt__modal-actions">
                        <button
                          type="button"
                          className="user-mgmt__btn user-mgmt__btn--reject user-mgmt__btn--lg"
                          disabled={actionBusy === detail.id}
                          onClick={() => onReject(detail.id)}
                        >
                          Reject access
                        </button>
                        <button
                          type="button"
                          className="user-mgmt__btn user-mgmt__btn--approve user-mgmt__btn--lg"
                          disabled={actionBusy === detail.id}
                          onClick={() => onApprove(detail.id, approveRole)}
                        >
                          Approve access
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
